/**
 * Reports Service
 * Typed API functions for all report endpoints.
 */
import { apiGet } from './api';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface ReportParams {
  startDate?: string;
  endDate?: string;
  programId?: number;
  clientType?: string;
  page?: number;
  pageSize?: number;
}

export interface ClientVisitRecord {
  visitDate: string;
  clientId: string;
  clientName: string;
  clientType: string;
  programName: string;
  caseNote: string;
  durationMinutes: number | null;
}

export interface ClientVisitsResponse {
  records: ClientVisitRecord[];
  summary: {
    totalVisits: number;
    uniqueClients: number;
    totalContactHours: number;
    programBreakdown: { programName: string; visitCount: number }[];
  };
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface StaffTimeRecord {
  staffId: number;
  staffName: string;
  staffEmail: string;
  visitCount: number;
  totalMinutes: number;
  totalHours: number;
}

export interface StaffTimeResponse {
  records: StaffTimeRecord[];
  summary: {
    totalStaff: number;
    totalHours: number;
    totalVisits: number;
  };
}

export interface CANSComplianceResponse {
  summary: {
    totalAssessments: number;
    compliant: number;
    overdue: number;
    redFlags: number;
    complianceRate: number;
  };
  overdueList: {
    clientId: string;
    clientName: string;
    assessmentId: number;
    assessmentType: string;
    assessmentDate: string;
    nextDueDate: string;
    daysOverdue: number;
  }[];
  redFlagList: {
    clientId: string;
    clientName: string;
    assessmentId: number;
    assessmentType: string;
    assessmentDate: string;
    nextDueDate: string;
  }[];
}

export interface DemographicsResponse {
  byAge: { ageGroup: string; count: number }[];
  byGender: { gender: string; count: number }[];
  byRace: { raceEthnicity: string; count: number }[];
  byZip: { zipCode: string; count: number }[];
  totalClients: number;
}

export interface DonorReportResponse {
  summary: {
    totalDonations: number;
    totalAmount: number;
    averageAmount: number;
    uniqueDonors: number;
  };
  byCampaign: { campaignName: string; donationCount: number; totalAmount: number }[];
  byPaymentMethod: { paymentMethod: string; donationCount: number; totalAmount: number }[];
  byDonorType: { donorType: string; donationCount: number; totalAmount: number }[];
}

export interface DashboardMetrics {
  totalClients: number;
  activeFamilies: number;
  serviceHours: number;
  cansDue: number;
  totalDonors: number;
  totalDonations: number;
  redFlags: number;
}

// ─── API Functions ──────────────────────────────────────────────────────────

export async function getDashboardMetrics(): Promise<DashboardMetrics> {
  const res = await apiGet<DashboardMetrics>('/reports/dashboard');
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch dashboard'); }
  return res.data as DashboardMetrics;
}

export async function getClientVisitsReport(params: ReportParams): Promise<ClientVisitsResponse> {
  const res = await apiGet<ClientVisitsResponse>('/reports/client-visits', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch client visits'); }
  return res.data as ClientVisitsResponse;
}

export async function getClientVisitsExport(params: ReportParams): Promise<{ records: ClientVisitRecord[]; totalRecords: number }> {
  const res = await apiGet<{ records: ClientVisitRecord[]; totalRecords: number }>('/reports/client-visits/export', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to export client visits'); }
  return res.data as { records: ClientVisitRecord[]; totalRecords: number };
}

export async function getStaffTimeReport(params: ReportParams): Promise<StaffTimeResponse> {
  const res = await apiGet<StaffTimeResponse>('/reports/staff-time', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch staff time'); }
  return res.data as StaffTimeResponse;
}

export async function getStaffTimeExport(params: ReportParams): Promise<{ records: StaffTimeRecord[]; totalRecords: number }> {
  const res = await apiGet<{ records: StaffTimeRecord[]; totalRecords: number }>('/reports/staff-time/export', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to export staff time'); }
  return res.data as { records: StaffTimeRecord[]; totalRecords: number };
}

export async function getCANSComplianceReport(): Promise<CANSComplianceResponse> {
  const res = await apiGet<CANSComplianceResponse>('/reports/cans-compliance');
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch CANS compliance'); }
  return res.data as CANSComplianceResponse;
}

export async function getCANSComplianceExport(): Promise<{ records: Record<string, unknown>[]; totalRecords: number }> {
  const res = await apiGet<{ records: Record<string, unknown>[]; totalRecords: number }>('/reports/cans-compliance/export');
  if (!res.success) { throw new Error(res.error?.message || 'Failed to export CANS compliance'); }
  return res.data as { records: Record<string, unknown>[]; totalRecords: number };
}

export async function getDemographicsReport(): Promise<DemographicsResponse> {
  const res = await apiGet<DemographicsResponse>('/reports/demographics');
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch demographics'); }
  return res.data as DemographicsResponse;
}

export async function getDemographicsExport(): Promise<{ records: Record<string, unknown>[]; totalRecords: number }> {
  const res = await apiGet<{ records: Record<string, unknown>[]; totalRecords: number }>('/reports/demographics/export');
  if (!res.success) { throw new Error(res.error?.message || 'Failed to export demographics'); }
  return res.data as { records: Record<string, unknown>[]; totalRecords: number };
}

export async function getDonorReport(params: ReportParams): Promise<DonorReportResponse> {
  const res = await apiGet<DonorReportResponse>('/reports/donor', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to fetch donor report'); }
  return res.data as DonorReportResponse;
}

export async function getDonorExport(params: ReportParams): Promise<{ records: Record<string, unknown>[]; totalRecords: number }> {
  const res = await apiGet<{ records: Record<string, unknown>[]; totalRecords: number }>('/reports/donor/export', params);
  if (!res.success) { throw new Error(res.error?.message || 'Failed to export donor report'); }
  return res.data as { records: Record<string, unknown>[]; totalRecords: number };
}
